// src/pages/Home.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Button, 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Grid,
  Container,
  alpha,
  useTheme,
  Chip,
  Fade,
  Grow,
  Paper
} from '@mui/material';
import {
  Palette as DesignerIcon,
  Explore as ExplorerIcon,
  Schema as OntologyIcon,
  Build as ServicesIcon,
  SportsEsports as GameIcon,
  RocketLaunch as WelcomeIcon,
  Star as StarIcon,
  AutoAwesome as SparkleIcon,
  // Новые иконки
  RocketLaunch as AssetIcon,      // Для Ангара
  Rule as RuleIcon,                 // Для Правил
  Psychology as LogicIcon           // Дополнительно для логики
} from '@mui/icons-material';

export default function Home() {
  const theme = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const features = [
    {
      title: "Дизайнер персонажей",
      description: "Создайте уникального персонажа: выберите расу, навыки, внешность и биографию для вашего космического приключения.",
      icon: DesignerIcon,
      path: "/designer",
      color: theme.palette.primary.main,
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      buttonText: "Создать персонажа",
      badge: "Популярное"
    },
    {
      title: "Ангар / Конструктор",
      description: "Проектирование кораблей и структур. Процедурная генерация модулей, расчет баланса и подготовка чертежей к запуску.",
      icon: AssetIcon,
      path: "/assets",
      color: "#00bcd4", // Cyan
      gradient: "linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)",
      buttonText: "Открыть ангар",
      badge: "Новое"
    },
    {
      title: "Исследователь миров",
      description: "Откройте тайны галактики: исследуйте звёздные системы, планеты и космические аномалии вселенной UEF.",
      icon: ExplorerIcon,
      path: "/worlds",
      color: theme.palette.secondary.main,
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      buttonText: "Исследовать"
    },
    {
      title: "Правила и Логика",
      description: "Управление системой событий. Настройка условий, триггеров и реакций искусственного интеллекта симуляции.",
      icon: RuleIcon,
      path: "/rules",
      color: "#9c27b0", // Purple
      gradient: "linear-gradient(135deg, #8E2DE2 0%, #4A00E0 100%)",
      buttonText: "Редактор правил"
    },
    {
      title: "Редактор онтологий",
      description: "Создавайте и настраивайте миры: разрабатывайте расы, технологии и правила игровой вселенной.",
      icon: OntologyIcon,
      path: "/ontologys",
      color: theme.palette.info.main,
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      buttonText: "Редактировать"
    },
    {
      title: "Тестирование сервисов",
      description: "Отлаживайте и тестируйте: проверяйте работу Phoenix-каналов, API и игровых событий в реальном времени.",
      icon: ServicesIcon,
      path: "/services",
      color: theme.palette.warning.main,
      gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
      buttonText: "Тестировать"
    },
    {
      title: "Игровой клиент",
      description: "Погрузитесь в игру: управляйте кораблём, взаимодействуйте с NPC и исследуйте космос в основном игровом интерфейсе.",
      icon: GameIcon,
      path: "/game",
      color: theme.palette.success.main,
      gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
      buttonText: "Начать игру",
      featured: true,
      badge: "Играть"
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: theme.palette.mode === 'dark' 
        ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Декоративные элементы фона */}
      <Box sx={{
        position: 'absolute',
        top: -100,
        right: -100,
        width: 400,
        height: 400,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
        filter: 'blur(60px)'
      }} />
      <Box sx={{
        position: 'absolute',
        bottom: -150,
        left: -150,
        width: 500,
        height: 500,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.1)} 0%, transparent 70%)`,
        filter: 'blur(80px)'
      }} />

      <Container maxWidth="xl" sx={{ py: 6, position: 'relative', zIndex: 1 }}>
        {/* Заголовок с анимацией */}
        <Fade in={mounted} timeout={1000}>
          <Paper 
            elevation={0}
            sx={{ 
              textAlign: 'center', 
              mb: 8,
              p: 6,
              borderRadius: 4,
              background: theme.palette.mode === 'dark'
                ? 'rgba(30, 41, 59, 0.7)'
                : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Анимированные звёзды на фоне */}
            <Box sx={{
              position: 'absolute',
              top: 20,
              right: 40,
              animation: 'twinkle 2s ease-in-out infinite'
            }}>
              <StarIcon sx={{ color: alpha(theme.palette.warning.main, 0.6), fontSize: 24 }} />
            </Box>
            <Box sx={{
              position: 'absolute',
              bottom: 30,
              left: 50,
              animation: 'twinkle 3s ease-in-out infinite 0.5s'
            }}>
              <SparkleIcon sx={{ color: alpha(theme.palette.secondary.main, 0.6), fontSize: 20 }} />
            </Box>

            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              mb: 3,
              boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.3)}`,
              animation: 'float 6s ease-in-out infinite'
            }}>
              <WelcomeIcon sx={{ fontSize: 50, color: 'white' }} />
            </Box>

            <Typography 
              variant="h2" 
              component="h1" 
              gutterBottom
              sx={{
                fontWeight: 800,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 50%, ${theme.palette.info.main} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                letterSpacing: '-0.02em',
                mb: 2
              }}
            >
              UEF — Вселенная
            </Typography>
            
            <Typography 
              variant="h5" 
              sx={{ 
                color: 'text.secondary',
                maxWidth: 700, 
                mx: 'auto',
                lineHeight: 1.6,
                fontWeight: 400
              }}
            >
              Расширяя границы реальности. Создавайте, исследуйте и управляйте 
              вашим космическим приключением в одном месте.
            </Typography>
          </Paper>
        </Fade>

        {/* Карточки функций с анимацией */}
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grow 
              in={mounted} 
              timeout={1000 + (index * 200)} 
              key={index}
            >
              <Grid item xs={12} sm={6} lg={4}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    borderRadius: 3,
                    border: 'none',
                    background: theme.palette.mode === 'dark' 
                      ? 'rgba(30, 41, 59, 0.6)'
                      : 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    overflow: 'visible',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-12px) scale(1.02)',
                      boxShadow: `0 25px 50px -12px ${alpha(feature.color, 0.25)}`,
                      '& .card-icon': {
                        transform: 'scale(1.1) rotate(5deg)',
                        boxShadow: `0 20px 40px ${alpha(feature.color, 0.4)}`
                      }
                    }
                  }}
                >
                  {/* Градиентная полоса сверху */}
                  <Box sx={{
                    height: 4,
                    background: feature.gradient,
                    borderRadius: '3px 3px 0 0'
                  }} />

                  {/* Бейдж */}
                  {feature.badge && (
                    <Chip
                      size="small"
                      label={feature.badge}
                      sx={{
                        position: 'absolute',
                        top: -10,
                        right: 20,
                        background: feature.gradient,
                        color: 'white',
                        fontWeight: 'bold',
                        boxShadow: `0 4px 12px ${alpha(feature.color, 0.4)}`,
                        zIndex: 2
                      }}
                    />
                  )}

                  <CardContent sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Иконка с градиентным фоном */}
                    <Box 
                      className="card-icon"
                      sx={{ 
                        width: 70,
                        height: 70,
                        borderRadius: 3,
                        background: feature.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: `0 10px 30px ${alpha(feature.color, 0.3)}`
                      }}
                    >
                      <feature.icon sx={{ fontSize: 35, color: 'white' }} />
                    </Box>

                    {/* Заголовок */}
                    <Typography 
                      variant="h5" 
                      component="h2" 
                      gutterBottom
                      sx={{ 
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        mb: 1.5
                      }}
                    >
                      {feature.title}
                    </Typography>

                    {/* Описание */}
                    <Typography 
                      variant="body1" 
                      color="text.secondary"
                      sx={{ 
                        mb: 3, 
                        flexGrow: 1,
                        lineHeight: 1.7,
                        fontSize: '0.95rem'
                      }}
                    >
                      {feature.description}
                    </Typography>

                    {/* Кнопка с градиентом */}
                    <Button 
                      component={Link}
                      to={feature.path}
                      variant="contained"
                      size="large"
                      fullWidth
                      endIcon={<SparkleIcon sx={{ fontSize: 18 }} />}
                      sx={{
                        mt: 'auto',
                        background: feature.featured ? feature.gradient : 'transparent',
                        color: feature.featured ? 'white' : feature.color,
                        border: feature.featured ? 'none' : `2px solid ${feature.color}`,
                        borderRadius: 2,
                        py: 1.5,
                        fontWeight: 700,
                        fontSize: '1rem',
                        textTransform: 'none',
                        boxShadow: feature.featured ? `0 10px 30px ${alpha(feature.color, 0.3)}` : 'none',
                        '&:hover': {
                          background: feature.gradient,
                          color: 'white',
                          boxShadow: `0 15px 35px ${alpha(feature.color, 0.4)}`,
                          transform: 'translateY(-2px)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {feature.buttonText}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grow>
          ))}
        </Grid>

        {/* Статистика или дополнительная информация */}
        <Fade in={mounted} timeout={2000}>
          <Paper
            elevation={0}
            sx={{
              mt: 8,
              p: 5,
              borderRadius: 4,
              textAlign: 'center',
              background: theme.palette.mode === 'dark'
                ? 'rgba(30, 41, 59, 0.5)'
                : 'rgba(255, 255, 255, 0.6)',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}
          >
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
              Нужна помощь?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto', fontSize: '1.1rem' }}>
              Ознакомьтесь с документацией или присоединитесь к нашему сообществу, 
              чтобы получить помощь и поделиться своими творениями.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              {[
                { icon: '📚', label: 'Документация' },
                { icon: '👥', label: 'Сообщество' },
                { icon: '🐛', label: 'Сообщить об ошибке' }
              ].map((item, idx) => (
                <Button 
                  key={idx}
                  variant="outlined"
                  size="large"
                  sx={{
                    borderRadius: 3,
                    px: 4,
                    py: 1.5,
                    borderWidth: 2,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1rem',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderWidth: 2,
                      transform: 'translateY(-3px)',
                      boxShadow: `0 10px 20px ${alpha(theme.palette.primary.main, 0.2)}`
                    }
                  }}
                >
                  <Box component="span" sx={{ mr: 1, fontSize: '1.3rem' }}>{item.icon}</Box>
                  {item.label}
                </Button>
              ))}
            </Box>
          </Paper>
        </Fade>
      </Container>

      {/* CSS анимации */}
      <style jsx global>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>
    </Box>
  );
}